import React, { useState, useEffect, useCallback } from 'react';
import { branchAPI, stockHandoverAPI } from '../../services/branchAPI';
import Toast from '../../components/Toast';
import './BranchStockHandover.css';
import apiService from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

function BranchStockHandover() {
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('handovers');
  const [loading, setLoading] = useState(false);

  // Branch State
  const [branches, setBranches] = useState([]);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [branchFormData, setBranchFormData] = useState({
    branchName: '',
    location: '',
    contactPerson: '',
    phone: '',
  });

  // Stock Handover State
  const [handovers, setHandovers] = useState([]);
  const [showHandoverForm, setShowHandoverForm] = useState(false);
  const [editingHandoverId, setEditingHandoverId] = useState(null);
  const [handoverFormData, setHandoverFormData] = useState({
    branchId: '',
    size: '1kg',
    quantity: '',
    handoverDate: new Date().toISOString().split('T')[0],
    notes: '',
    paid: '',
    newPayment: '',
  });

  const [filterBranch, setFilterBranch] = useState('all');
  const [filterSize, setFilterSize] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const [stockTotals, setStockTotals] = useState({ '1kg': 0, '500g': 0, '200g': 0 });



  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await branchAPI.getAllBranches();
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast('Failed to fetch branches', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHandovers = useCallback(async () => {
    try {
      const data = await stockHandoverAPI.getAllHandovers();
      setHandovers(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast('Failed to fetch handovers', 'error');
    }
  }, []);

  const fetchStockTotals = useCallback(async () => {
    try {
      const data = await apiService.getAllStocks();
      const totals = { '1kg': 0, '500g': 0, '200g': 0 };
      data.forEach(stock => {
        if (totals.hasOwnProperty(stock.size)) {
          totals[stock.size] += stock.quantity;
        }
      });
      setStockTotals(totals);
    } catch (error) {
      console.error('Failed to fetch stock totals', error);
    }
  }, []);


  // Fetch all data on component mount
  useEffect(() => {
    fetchBranches();
    fetchHandovers();
    fetchStockTotals(); // 👈 add this
  }, [fetchBranches, fetchHandovers, fetchStockTotals]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Branch Form Handlers
  const handleBranchInputChange = (e) => {
    const { name, value } = e.target;
    setBranchFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();

    if (!branchFormData.branchName || !branchFormData.location) {
      showToast('Branch name and location are required', 'error');
      return;
    }

    try {
      setLoading(true);
      if (editingBranchId) {
        await branchAPI.updateBranch(editingBranchId, branchFormData);
        showToast('Branch updated successfully', 'success');
      } else {
        await branchAPI.addBranch(branchFormData);
        showToast('Branch added successfully', 'success');
      }

      setBranchFormData({ branchName: '', location: '', contactPerson: '', phone: '' });
      setEditingBranchId(null);
      setShowBranchForm(false);
      fetchBranches();
    } catch (error) {
      showToast(error.message || 'Failed to save branch', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBranch = (branch) => {
    setBranchFormData({
      branchName: branch.branchName,
      location: branch.location,
      contactPerson: branch.contactPerson || '',
      phone: branch.phone || '',
    });
    setEditingBranchId(branch.id);
    setShowBranchForm(true);
  };

  const handleDeleteBranch = async (id) => {
    if (window.confirm('Are you sure you want to delete this branch?')) {
      try {
        setLoading(true);
        await branchAPI.deleteBranch(id);
        showToast('Branch deleted successfully', 'success');
        fetchBranches();
      } catch (error) {
        showToast(error.message || 'Failed to delete branch', 'error');
      } finally {
        setLoading(false);
      }
    }
  };



  const handleCancelBranch = () => {
    setShowBranchForm(false);
    setEditingBranchId(null);
    setBranchFormData({ branchName: '', location: '', contactPerson: '', phone: '' });
  };

  // Handover Form Handlers
  const handleHandoverInputChange = (e) => {
    const { name, value } = e.target;
    setHandoverFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleHandoverSubmit = async (e) => {
  e.preventDefault();

  if (!handoverFormData.branchId || !handoverFormData.quantity) {
    showToast('Please select a branch and enter quantity', 'error');
    return;
  }

  if (handoverFormData.quantity <= 0) {
    showToast('Quantity must be greater than 0', 'error');
    return;
  }

  if (editingHandoverId) {
    if (handoverFormData.paid && !isValidPaymentAmount(handoverFormData.paid, handoverFormData.size)) {
      showToast(`Total Paid must be a multiple of Rs. ${PRICE_PER_UNIT[handoverFormData.size]}`, 'error');
      return;
    }
    if (handoverFormData.newPayment && !isValidPaymentAmount(handoverFormData.newPayment, handoverFormData.size)) {
      showToast(`New Payment must be a multiple of Rs. ${PRICE_PER_UNIT[handoverFormData.size]}`, 'error');
      return;
    }
  }

  try {
    setLoading(true);
    const totalPaid = (parseFloat(handoverFormData.paid) || 0) + (parseFloat(handoverFormData.newPayment) || 0);
    const fullPaymentCalc = PRICE_PER_UNIT[handoverFormData.size] * parseInt(handoverFormData.quantity, 10);

    const payload = {
      branchId: parseInt(handoverFormData.branchId, 10),
      size: handoverFormData.size,
      quantity: parseInt(handoverFormData.quantity, 10),
      handoverDate: new Date(handoverFormData.handoverDate).toISOString(),
      notes: handoverFormData.notes || null,
      fullPayment: fullPaymentCalc,
      ...(editingHandoverId && {
        paid: totalPaid,
        outstanding: fullPaymentCalc - totalPaid,
      }),
    };

    if (editingHandoverId) {
      await stockHandoverAPI.updateHandover(editingHandoverId, payload);
      showToast('Stock handover updated successfully', 'success');
    } else {
      await stockHandoverAPI.addHandover(payload);
      showToast('Stock handover added successfully', 'success');
    }

    setHandoverFormData({
      branchId: '',
      size: '1kg',
      quantity: '',
      handoverDate: new Date().toISOString().split('T')[0],
      notes: '',
      paid: '',
      newPayment: '',
    });
    setEditingHandoverId(null);
    setShowHandoverForm(false);
    fetchHandovers();
    fetchBranches();
  } catch (error) {
    showToast(error.message || 'Failed to save handover', 'error');
  } finally {
    setLoading(false);
  }
};
  const handleEditHandover = (handover) => {
    const handoverDate = handover.handoverDate
      ? new Date(handover.handoverDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

    const fullPayment = PRICE_PER_UNIT[handover.size] * handover.quantity;

    setHandoverFormData({
      branchId: handover.branchId,
      size: handover.size,
      quantity: handover.quantity,
      handoverDate,
      notes: handover.notes || '',
      paid: handover.paid ?? '',
      newPayment: '',
      fullPayment,
    });
    setEditingHandoverId(handover.id);
    setShowHandoverForm(true);
  };

  const handleDeleteHandover = async (id) => {
    if (window.confirm('Are you sure you want to delete this handover record?')) {
      try {
        setLoading(true);
        await stockHandoverAPI.deleteHandover(id);
        showToast('Handover deleted successfully', 'success');
        fetchHandovers();
        fetchBranches();
      } catch (error) {
        showToast(error.message || 'Failed to delete handover', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelHandover = () => {
    setShowHandoverForm(false);
    setEditingHandoverId(null);
    setHandoverFormData({
      branchId: '',
      size: '1kg',
      quantity: '',
      handoverDate: new Date().toISOString().split('T')[0],
      notes: '',
      paid: '',
      newPayment: '',
    });
  };

  // Helper functions
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };


  const calculateOutstandingUnits = (handover) => {
    if (handover.outstanding == null || handover.outstanding <= 0) return 0;
    const pricePerUnit = PRICE_PER_UNIT[handover.size];
    if (!pricePerUnit) return 0;
    return Math.round(handover.outstanding / pricePerUnit);
  };
  const getFilterLabel = () => {
    const parts = [];
    if (filterBranch !== 'all') {
      const branch = branches.find(b => b.id === parseInt(filterBranch));
      if (branch) parts.push(`Branch: ${branch.branchName}`);
    }
    if (filterSize !== 'all') parts.push(`Size: ${filterSize}`);
    if (filterMonth) parts.push(`Month: ${new Date(2000, parseInt(filterMonth) - 1).toLocaleString('en-US', { month: 'long' })}`);
    if (filterYear) parts.push(`Year: ${filterYear}`);
    return parts.length > 0 ? parts.join(' | ') : 'All Records';
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');

    doc.setFontSize(18);
    doc.setTextColor(44, 62, 80);
    doc.text('Stock Distribution Report', 14, 20);

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`Filter: ${getFilterLabel()}`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, 37);

    // Distribution summary
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text('Distribution Summary', 14, 48);

    autoTable(doc, {
      startY: 52,
      head: [['Size', 'Units Distributed', 'Full Payment (Rs.)', 'Paid (Rs.)', 'Outstanding (Rs.)']],
      body: [
        ['1kg', filteredDistributed['1kg'], '-', '-', '-'],
        ['500g', filteredDistributed['500g'], '-', '-', '-'],
        ['200g', filteredDistributed['200g'], '-', '-', '-'],
        [
          'TOTAL',
          filteredDistributed['1kg'] + filteredDistributed['500g'] + filteredDistributed['200g'],
          `Rs. ${filteredPaymentTotals.fullPayment.toLocaleString()}`,
          `Rs. ${filteredPaymentTotals.paid.toLocaleString()}`,
          `Rs. ${filteredPaymentTotals.outstanding.toLocaleString()}`,
        ],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [142, 68, 173] },
      alternateRowStyles: { fillColor: [248, 245, 252] },
      bodyStyles: { textColor: [44, 62, 80] },
    });

    // Records table
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text('Distribution Records', 14, doc.lastAutoTable.finalY + 12);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 16,
      head: [['ID', 'Branch', 'Location', 'Size', 'Qty', 'Date', 'Status', 'Full Pay', 'Paid', 'Outstanding', 'Notes']],
      body: filteredHandovers.map(h => [
        h.id,
        h.branch?.branchName || 'Unknown',
        h.branch?.location || '-',
        h.size,
        h.quantity,
        formatDate(h.handoverDate),
        h.status,
        h.fullPayment != null ? `Rs. ${h.fullPayment.toLocaleString()}` : '-',
        h.paid != null ? `Rs. ${h.paid.toLocaleString()}` : '-',
        h.outstanding != null ? `Rs. ${h.outstanding.toLocaleString()}` : '-',
        h.notes || '-',
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [142, 68, 173] },
      alternateRowStyles: { fillColor: [248, 245, 252] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 28 },
        2: { cellWidth: 28 },
        3: { cellWidth: 14 },
        4: { cellWidth: 12 },
        5: { cellWidth: 35 },
        6: { cellWidth: 22 },
        7: { cellWidth: 22 },
        8: { cellWidth: 20 },
        9: { cellWidth: 24 },
        10: { cellWidth: 'auto' },
      },
    });

    doc.save(`distribution-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Stock Distribution Report'],
      [`Filter: ${getFilterLabel()}`],
      [`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`],
      [],
      ['Distribution Summary'],
      ['Size', 'Units Distributed'],
      ['1kg', filteredDistributed['1kg']],
      ['500g', filteredDistributed['500g']],
      ['200g', filteredDistributed['200g']],
      ['Total Units', filteredDistributed['1kg'] + filteredDistributed['500g'] + filteredDistributed['200g']],
      [],
      ['Payment Summary'],
      ['', 'Amount (Rs.)'],
      ['Total Full Payment', filteredPaymentTotals.fullPayment],
      ['Total Paid', filteredPaymentTotals.paid],
      ['Total Outstanding', filteredPaymentTotals.outstanding],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 20 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Records sheet
    const recordsData = [
      ['ID', 'Branch', 'Location', 'Size', 'Quantity', 'Handover Date', 'Status', 'Full Payment (Rs.)', 'Paid (Rs.)', 'Outstanding (Rs.)', 'Notes'],
      ...filteredHandovers.map(h => [
        h.id,
        h.branch?.branchName || 'Unknown',
        h.branch?.location || '-',
        h.size,
        h.quantity,
        formatDate(h.handoverDate),
        h.status,
        h.fullPayment ?? '-',
        h.paid ?? '-',
        h.outstanding ?? '-',
        h.notes || '-',
      ]),
    ];
    const wsRecords = XLSX.utils.aoa_to_sheet(recordsData);
    wsRecords['!cols'] = [
      { wch: 8 }, { wch: 20 }, { wch: 20 }, { wch: 10 },
      { wch: 10 }, { wch: 24 }, { wch: 16 },
      { wch: 20 }, { wch: 16 }, { wch: 20 }, { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, wsRecords, 'Distribution Records');

    XLSX.writeFile(wb, `distribution-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };
  const PRICE_PER_UNIT = {
    '1kg': 1300,
    '500g': 700,
    '200g': 400,
  };

  const getYearOptions = () => {
    const years = [...new Set(handovers.map(h => new Date(h.handoverDate).getFullYear()))];
    return years.sort((a, b) => b - a);
  };

  const calculateBranchStock = (branchId) => {
    const branchHandovers = handovers.filter(h => h.branchId === branchId);
    return {
      '1kg': branchHandovers.filter(h => h.size === '1kg').reduce((sum, h) => sum + h.quantity, 0),
      '500g': branchHandovers.filter(h => h.size === '500g').reduce((sum, h) => sum + h.quantity, 0),
      '200g': branchHandovers.filter(h => h.size === '200g').reduce((sum, h) => sum + h.quantity, 0),
    };
  };



  const filteredHandovers = handovers.filter(handover => {
    if (filterBranch !== 'all' && handover.branchId !== parseInt(filterBranch, 10)) return false;
    if (filterSize !== 'all' && handover.size !== filterSize) return false;
    if (filterMonth || filterYear) {
      const date = new Date(handover.handoverDate);
      if (filterMonth && date.getMonth() + 1 !== parseInt(filterMonth)) return false;
      if (filterYear && date.getFullYear() !== parseInt(filterYear)) return false;
    }
    return true;
  });

  const totalDistributed = {
    '1kg': handovers.filter(h => h.size === '1kg').reduce((sum, h) => sum + h.quantity, 0),
    '500g': handovers.filter(h => h.size === '500g').reduce((sum, h) => sum + h.quantity, 0),
    '200g': handovers.filter(h => h.size === '200g').reduce((sum, h) => sum + h.quantity, 0),
  };

  const filteredDistributed = {
    '1kg': filteredHandovers.filter(h => h.size === '1kg').reduce((sum, h) => sum + h.quantity, 0),
    '500g': filteredHandovers.filter(h => h.size === '500g').reduce((sum, h) => sum + h.quantity, 0),
    '200g': filteredHandovers.filter(h => h.size === '200g').reduce((sum, h) => sum + h.quantity, 0),
  };
  // const filteredPaymentTotals = {
  //   fullPayment: filteredHandovers.reduce((sum, h) => sum + (h.fullPayment || 0), 0),
  //   paid: filteredHandovers.reduce((sum, h) => sum + (h.paid || 0), 0),
  //   outstanding: filteredHandovers.reduce((sum, h) => sum + (h.outstanding || 0), 0),
  // };



  const getFullPayment = (handover) => {
    if (handover.fullPayment != null) return handover.fullPayment;
    const pricePerUnit = PRICE_PER_UNIT[handover.size];
    if (!pricePerUnit) return null;
    return pricePerUnit * handover.quantity;
  };

  const filteredPaymentTotals = {
    fullPayment: filteredHandovers.reduce((sum, h) => sum + (getFullPayment(h) || 0), 0),
    paid: filteredHandovers.reduce((sum, h) => sum + (h.paid || 0), 0),
    outstanding: filteredHandovers.reduce((sum, h) => sum + (h.outstanding || 0), 0),
  };


  const filteredOutstandingUnits = filteredHandovers.reduce((sum, h) => {
    return sum + calculateOutstandingUnits(h);
  }, 0);
  const filteredOutstandingUnitsBySize = (() => {
    const totals = { '1kg': 0, '500g': 0, '200g': 0 };
    filteredHandovers.forEach(h => {
      const units = calculateOutstandingUnits(h);
      if (totals.hasOwnProperty(h.size)) totals[h.size] += units;
    });
    return totals;
  })();

  const isValidPaymentAmount = (amount, size) => {
    const pricePerUnit = PRICE_PER_UNIT[size];
    if (!pricePerUnit || !amount) return true; // empty is fine, let required handle it
    return Number(amount) % pricePerUnit === 0;
  };



  return (
    <div className="branch-stock-handover-container">
      <div className="branch-header">
        <h2> Branch Stock Distribution</h2>
      </div>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Tab Navigation */}
      <div className="branch-tabs">
        <button
          className={`tab-button ${activeTab === 'handovers' ? 'active' : ''}`}
          onClick={() => setActiveTab('handovers')}
        >
          Stock Distribution
        </button>
        <button
          className={`tab-button ${activeTab === 'branches' ? 'active' : ''}`}
          onClick={() => setActiveTab('branches')}
        >
          Branch Management
        </button>
      </div>

      {/* ==================== BRANCH MANAGEMENT TAB ==================== */}
      {activeTab === 'branches' && (
        <div className="branch-tab-content">
          <div className="section-header">
            <h3>Branch Directory</h3>
            <button
              className="btn-add-branch"
              onClick={() => setShowBranchForm(true)}
              disabled={loading}
            >
              ➕ Add Branch
            </button>
          </div>

          {showBranchForm && (
            <div className="branch-form-container">
              <div className="branch-form-content">
                <h3>{editingBranchId ? 'Edit Branch' : 'Add New Branch'}</h3>
                <form onSubmit={handleBranchSubmit}>
                  <div className="form-group">
                    <label htmlFor="branchName">Branch Name:</label>
                    <input id="branchName" type="text" name="branchName"
                      value={branchFormData.branchName} onChange={handleBranchInputChange}
                      placeholder="Enter branch name" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="location">Location:</label>
                    <input id="location" type="text" name="location"
                      value={branchFormData.location} onChange={handleBranchInputChange}
                      placeholder="Enter location" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactPerson">Contact Person:</label>
                    <input id="contactPerson" type="text" name="contactPerson"
                      value={branchFormData.contactPerson} onChange={handleBranchInputChange}
                      placeholder="Enter contact person name" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone:</label>
                    <input id="phone" type="tel" name="phone"
                      value={branchFormData.phone} onChange={handleBranchInputChange}
                      placeholder="Enter phone number" />
                  </div>
                  <div className="form-buttons">
                    <button type="submit" className="btn-submit" disabled={loading}>
                      {loading ? 'Saving...' : editingBranchId ? 'Update Branch' : 'Add Branch'}
                    </button>
                    <button type="button" className="btn-cancel" onClick={handleCancelBranch}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {loading && !showBranchForm ? (
            <div className="loading">Loading branches...</div>
          ) : branches.length === 0 ? (
            <div className="no-records">No branches found. Add your first branch!</div>
          ) : (
            <>
              <div className="branch-summary">
                <div className="branch-summary-card">
                  <h4>Total Branches</h4>
                  <p className="summary-value">{branches.length}</p>
                  <span className="summary-label">active locations</span>
                </div>
                <div className="branch-summary-card">
                  <h4>Total 1kg Distributed</h4>
                  <p className="summary-value">{totalDistributed['1kg']}</p>
                  <span className="summary-label">units across branches</span>
                </div>
                <div className="branch-summary-card">
                  <h4>Total 500g Distributed</h4>
                  <p className="summary-value">{totalDistributed['500g']}</p>
                  <span className="summary-label">units across branches</span>
                </div>
                <div className="branch-summary-card">
                  <h4>Total 200g Distributed</h4>
                  <p className="summary-value">{totalDistributed['200g']}</p>
                  <span className="summary-label">units across branches</span>
                </div>
              </div>

              <div className="branch-table-wrapper">
                <table className="branch-table">
                  <thead>
                    <tr>
                      <th>ID</th><th>Branch Name</th><th>Location</th>
                      <th>Contact Person</th><th>Phone</th><th>Status</th>
                      <th>Created Date</th><th>1kg Stock</th><th>500g Stock</th>
                      <th>200g Stock</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.map(branch => {
                      const stock = calculateBranchStock(branch.id);
                      return (
                        <tr key={branch.id}>
                          <td>{branch.id}</td>
                          <td>{branch.branchName}</td>
                          <td>{branch.location}</td>
                          <td>{branch.contactPerson || '-'}</td>
                          <td>{branch.phone || '-'}</td>
                          <td>
                            <span className={`status-badge status-${branch.status}`}>
                              {branch.status}
                            </span>
                          </td>
                          <td>{formatDate(branch.createdDate)}</td>
                          <td><span className="stock-count-1kg">{stock['1kg']}</span></td>
                          <td><span className="stock-count-500g">{stock['500g']}</span></td>
                          <td><span className="stock-count-200g">{stock['200g']}</span></td>
                          <td className="action-buttons">
                            <button className="btn-edit" onClick={() => handleEditBranch(branch)} title="Edit">✏️</button>
                            <button className="btn-delete" onClick={() => handleDeleteBranch(branch.id)} title="Delete">🗑️</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ==================== STOCK DISTRIBUTION TAB ==================== */}
      {activeTab === 'handovers' && (
        <div className="handover-tab-content">

          {/* Stock Totals Banner */}
          {/* Stock Totals Banner */}
          <div className="stock-totals-banner">
            <div className="stock-total-card total-1kg">
              <span className="stock-total-icon"></span>
              <div>
                <p className="stock-total-value">{filteredDistributed['1kg']}</p>
                <span className="stock-total-label">1kg distributed</span>
              </div>
            </div>
            <div className="stock-total-card total-500g">
              <span className="stock-total-icon"></span>
              <div>
                <p className="stock-total-value">{filteredDistributed['500g']}</p>
                <span className="stock-total-label">500g distributed</span>
              </div>
            </div>
            <div className="stock-total-card total-200g">
              <span className="stock-total-icon"></span>
              <div>
                <p className="stock-total-value">{filteredDistributed['200g']}</p>
                <span className="stock-total-label">200g distributed</span>
              </div>
            </div>
          </div>

          {/* Payment Totals Banner */}
          <div className="payment-totals-banner">
            <div className="payment-total-card total-fullpayment">
              <span className="payment-total-icon"></span>
              <div>
                <p className="payment-total-value">Rs. {filteredPaymentTotals.fullPayment.toLocaleString()}</p>
                <span className="payment-total-label">Total Full Payment</span>
              </div>
            </div>
            <div className="payment-total-card total-paid">
              <span className="payment-total-icon"></span>
              <div>
                <p className="payment-total-value">Rs. {filteredPaymentTotals.paid.toLocaleString()}</p>
                <span className="payment-total-label">Total Paid</span>
              </div>
            </div>
            <div className="payment-total-card total-outstanding">
              <span className="payment-total-icon"></span>
              <div>
                <p className="payment-total-value">Rs. {filteredPaymentTotals.outstanding.toLocaleString()}</p>
                <span className="payment-total-label">Total Outstanding</span>
              </div>
            </div>
          </div>
          {/* Outstanding Units Banner */}
          {/* Outstanding Units Banner */}
          <div className="stock-totals-banner">
            <div className="stock-total-card total-1kg">
              <span className="stock-total-icon"></span>
              <div>
                <p className="stock-total-value">{filteredOutstandingUnitsBySize['1kg']}</p>
                <span className="stock-total-label">1kg outstanding units</span>
              </div>
            </div>
            <div className="stock-total-card total-500g">
              <span className="stock-total-icon"></span>
              <div>
                <p className="stock-total-value">{filteredOutstandingUnitsBySize['500g']}</p>
                <span className="stock-total-label">500g outstanding units</span>
              </div>
            </div>
            <div className="stock-total-card total-200g">
              <span className="stock-total-icon"></span>
              <div>
                <p className="stock-total-value">{filteredOutstandingUnitsBySize['200g']}</p>
                <span className="stock-total-label">200g outstanding units</span>
              </div>
            </div>
          </div>

          <div className="section-header">
            <h3>Stock Distribution Details</h3>
            <button
              className="btn-add-handover"
              onClick={() => setShowHandoverForm(true)}
              disabled={loading || branches.length === 0}
            >
              📤 Distribute Stock
            </button>
          </div>

          {branches.length === 0 && (
            <div className="warning-message">
              ⚠️ Please create branches first before distributing stock.
            </div>
          )}

          {showHandoverForm && (
            <div className="handover-form-container">
              <div className="handover-form-content">
                <h3>{editingHandoverId ? 'Edit Stock Distribution' : 'Distribute Stock to Branch'}</h3>
                <form onSubmit={handleHandoverSubmit}>
                  <div className="form-group">
                    <label htmlFor="branchId">Select Branch:</label>
                    <select id="branchId" name="branchId" value={handoverFormData.branchId}
                      onChange={handleHandoverInputChange} required>
                      <option value="">-- Select a branch --</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>
                          {branch.branchName} ({branch.location})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="size">Size:</label>
                    <select id="size" name="size" value={handoverFormData.size}
                      onChange={handleHandoverInputChange} required>
                      <option value="1kg">1kg</option>
                      <option value="500g">500g</option>
                      <option value="200g">200g</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="quantity">Quantity:</label>
                    <input id="quantity" type="number" name="quantity"
                      value={handoverFormData.quantity} onChange={handleHandoverInputChange}
                      placeholder="Enter quantity" required min="1" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="handoverDate">Distribution Date:</label>
                    <input id="handoverDate" type="date" name="handoverDate"
                      value={handoverFormData.handoverDate} onChange={handleHandoverInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="notes">Notes (Optional):</label>
                    <textarea id="notes" name="notes" value={handoverFormData.notes}
                      onChange={handleHandoverInputChange} placeholder="Add any notes..." rows="3" />
                  </div>

                  {/* 👇 ADD HERE — only shows when editing */}
                  {editingHandoverId && (
                    <>
                      <div className="payment-divider">
                        <span>💳 Payment Details</span>
                      </div>
                      <div className="form-group">
                        <label>Full Payment (Auto-calculated):</label>
                        <div className="payment-readonly">
                          <span className="payment-currency">Rs.</span>
                          <span className="payment-amount">
                            {handoverFormData.size && handoverFormData.quantity
                              ? (PRICE_PER_UNIT[handoverFormData.size] * parseInt(handoverFormData.quantity || 0)).toLocaleString()
                              : '0'}
                          </span>
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="paid">
                          Total Paid (Rs.) — multiple of Rs. {PRICE_PER_UNIT[handoverFormData.size]?.toLocaleString()}:
                        </label>
                        <input
                          id="paid"
                          type="number"
                          name="paid"
                          value={handoverFormData.paid}
                          onChange={handleHandoverInputChange}
                          placeholder={`e.g. ${PRICE_PER_UNIT[handoverFormData.size]}, ${PRICE_PER_UNIT[handoverFormData.size] * 2}...`}
                          min="0"
                          step={PRICE_PER_UNIT[handoverFormData.size]}
                        />
                        {handoverFormData.paid && !isValidPaymentAmount(handoverFormData.paid, handoverFormData.size) && (
                          <span className="payment-error">
                            ⚠️ Must be a multiple of Rs. {PRICE_PER_UNIT[handoverFormData.size]?.toLocaleString()}
                          </span>
                        )}
                      </div>


                      <div className="form-group">
                        <label>Already Paid (Rs.):</label>
                        <div className="payment-readonly">
                          <span className="payment-currency">Rs.</span>
                          <span className="payment-amount">
                            {(parseFloat(handoverFormData.paid) || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="newPayment">
                          Add New Payment (Rs.) {PRICE_PER_UNIT[handoverFormData.size]?.toLocaleString()}:
                        </label>
                        <input
                          id="newPayment"
                          type="number"
                          name="newPayment"
                          value={handoverFormData.newPayment}
                          onChange={handleHandoverInputChange}
                          placeholder={`e.g. ${PRICE_PER_UNIT[handoverFormData.size]}, ${PRICE_PER_UNIT[handoverFormData.size] * 2}, ${PRICE_PER_UNIT[handoverFormData.size] * 3}...`}
                          min="0"
                          step={PRICE_PER_UNIT[handoverFormData.size]}
                        />
                        {handoverFormData.newPayment && !isValidPaymentAmount(handoverFormData.newPayment, handoverFormData.size) && (
                          <span className="payment-error">
                            ⚠️ Amount must be a multiple of Rs. {PRICE_PER_UNIT[handoverFormData.size]?.toLocaleString()} (e.g. {PRICE_PER_UNIT[handoverFormData.size]}, {PRICE_PER_UNIT[handoverFormData.size] * 2}, {PRICE_PER_UNIT[handoverFormData.size] * 3})
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Outstanding (Auto-calculated):</label>
                        <div className={`payment-readonly ${(
                          (PRICE_PER_UNIT[handoverFormData.size] * parseInt(handoverFormData.quantity || 0)) -
                          ((parseFloat(handoverFormData.paid) || 0) + (parseFloat(handoverFormData.newPayment) || 0))
                        ) > 0
                          ? 'outstanding-pending'
                          : 'outstanding-clear'
                          }`}>
                          <span className="payment-currency">Rs.</span>
                          <span className="payment-amount">
                            {(
                              (PRICE_PER_UNIT[handoverFormData.size] * parseInt(handoverFormData.quantity || 0)) -
                              ((parseFloat(handoverFormData.paid) || 0) + (parseFloat(handoverFormData.newPayment) || 0))
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="form-buttons">
                    <button type="submit" className="btn-submit" disabled={loading}>
                      {loading ? 'Saving...' : editingHandoverId ? 'Update Distribution' : 'Distribute Stock'}
                    </button>
                    <button type="button" className="btn-cancel" onClick={handleCancelHandover}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="handover-filter">
            <label htmlFor="filterBranch">Branch:</label>
            <select id="filterBranch" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
              <option value="all">All Branches</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.branchName}</option>
              ))}
            </select>

            <label htmlFor="filterSize">Size:</label>
            <select id="filterSize" value={filterSize} onChange={(e) => setFilterSize(e.target.value)}>
              <option value="all">All Sizes</option>
              <option value="1kg">1kg</option>
              <option value="500g">500g</option>
              <option value="200g">200g</option>
            </select>

            <label htmlFor="filterMonth">Month:</label>
            <select id="filterMonth" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
              <option value="">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>

            <label htmlFor="filterYear">Year:</label>
            <select id="filterYear" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
              <option value="">All Years</option>
              {getYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            {(filterBranch !== 'all' || filterSize !== 'all' || filterMonth || filterYear) && (
              <button
                onClick={() => { setFilterBranch('all'); setFilterSize('all'); setFilterMonth(''); setFilterYear(''); }}
                style={{
                  padding: '8px 12px', borderRadius: '6px', border: '1px solid #bdc3c7',
                  background: '#ecf0f1', cursor: 'pointer', fontSize: '13px'
                }}
              >
                ✕ Clear
              </button>
            )}

            <span className="handover-count">
              Showing: {filteredHandovers.length} of {handovers.length} records
            </span>
            <div className="export-buttons">
              <button className="btn-export btn-export-pdf" onClick={exportToPDF}>
                📄 Export PDF
              </button>
              <button className="btn-export btn-export-excel" onClick={exportToExcel}>
                📊 Export Excel
              </button>
            </div>
          </div>

          {loading && !showHandoverForm ? (
            <div className="loading">Loading distribution records...</div>
          ) : filteredHandovers.length === 0 ? (
            <div className="no-records">No stock distribution records found</div>
          ) : (
            <div className="handover-table-wrapper">
              <table className="handover-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Branch</th><th>Location</th><th>Size</th>
                    <th>Quantity</th><th>Handover Date</th><th>Status</th>
                    <th>Full Payment</th><th>Paid</th><th>Outstanding</th>
                    <th>Notes</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHandovers.map(handover => (
                    <tr key={handover.id}>
                      <td>{handover.id}</td>
                      <td>{handover.branch?.branchName || 'Unknown'}</td>
                      <td>{handover.branch?.location || '-'}</td>
                      <td>
                        <span className={`size-badge size-${handover.size.replace(/\s+/g, '')}`}>
                          {handover.size}
                        </span>
                      </td>
                      <td>{handover.quantity}</td>
                      <td>{formatDate(handover.handoverDate)}</td>
                      <td>
                        <span className={`status-badge status-${handover.status.replace(/\s+/g, '')}`}>
                          {handover.status}
                        </span>
                      </td>
                      <td>
                        {getFullPayment(handover) != null
                          ? <span className="payment-cell">Rs. {getFullPayment(handover).toLocaleString()}</span>
                          : <span className="payment-empty">-</span>}
                      </td>
                      {/* <td>
                        {handover.fullPayment != null
                          ? <span className="payment-cell">Rs. {handover.fullPayment.toLocaleString()}</span>
                          : <span className="payment-empty">-</span>}
                      </td> */}
                      <td>
                        {handover.paid != null
                          ? <span className="paid-cell">Rs. {handover.paid.toLocaleString()}</span>
                          : <span className="payment-empty">-</span>}
                      </td>
                      <td>
                        {handover.outstanding != null
                          ? (
                            <span className={`outstanding-cell ${handover.outstanding > 0 ? 'outstanding-pending' : 'outstanding-clear'}`}>
                              Rs. {handover.outstanding.toLocaleString()}
                              {handover.outstanding > 0 && (
                                <span className="outstanding-units">
                                  {' '}({calculateOutstandingUnits(handover)} {handover.size})
                                </span>
                              )}
                            </span>
                          )
                          : <span className="payment-empty">-</span>}
                      </td>
                      <td>{handover.notes || '-'}</td>
                      <td className="action-buttons">
                        <button className="btn-edit" onClick={() => handleEditHandover(handover)} title="Edit">✏️</button>
                        <button className="btn-delete" onClick={() => handleDeleteHandover(handover.id)} title="Delete">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BranchStockHandover;